import { DateTime } from 'luxon';

import { P1D, PT30M } from '../constants';
import logger from '../logger';

/**
 * Trade Set Summary Batch Size Calculator
 *
 * This module calculates optimal batch sizes for trade set summary queries based on
 * measurable system constraints rather than arbitrary thresholds.
 *
 * ## System Constraints (Measured/Documented):
 *
 * 1. **Backend Query Timeout**: 30 seconds (observed in dataBatching.jsx:396)
 *    - We target 20 seconds to leave safety margin for network latency
 *
 * 2. **Frontend Timeout**: 60 seconds (DEFAULT_FETCH_QUERY_TIMEOUT)
 *    - Backend timeout is the limiting factor
 *
 * 3. **Response Payload Size**: ~1 MB practical limit
 *    - Based on browser memory constraints and JSON parsing performance
 *    - Each TradeSetSummary record is approximately 125 bytes
 *
 * 4. **GraphQL Complexity**: No hard limit documented, but query time scales with
 *    both record count AND backend aggregation complexity
 *
 * ## Data Volume Model:
 *
 * For a trade set summary query:
 * - **Records per batch** = ruleCount × intervalsPerBatch
 * - **Bytes per record** ≈ 125 bytes (average, includes all TradeSetSummary fields)
 *
 * ### PT30M Aggregation (used for ≤7 days):
 * - 48 intervals per day (24 hours ÷ 0.5 hours)
 * - **Backend work**: Minimal aggregation (raw interval data)
 * - **Query time**: ~0.5ms per record (empirically observed)
 * - **Limiting factor**: Payload size (many records returned)
 * - Example: 100 rules × 48 intervals = 4,800 records/day
 *   - Query time: 4,800 × 0.5ms = 2.4 seconds
 *   - Payload size: 4,800 × 125 bytes ≈ 600 KB
 *
 * ### P1D Aggregation (used for >7 days):
 * - 1 interval per day
 * - **Backend work**: Heavy aggregation (aggregates ~48 PT30M intervals into 1 P1D record)
 * - **Query time**: ~10ms per record (20× slower due to aggregation cost)
 * - **Limiting factor**: Backend query time (aggregation is expensive)
 * - Example: 100 rules × 30 days = 3,000 records/month
 *   - Query time: 3,000 × 10ms = 30 seconds
 *   - Payload size: 3,000 × 125 bytes ≈ 375 KB
 *
 * ## Aggregation-Specific Constraints:
 *
 * ### PT30M:
 * - Query time per record: 0.5ms (no aggregation)
 * - Max records from time: 20,000ms ÷ 0.5ms = 40,000 records
 * - Max records from payload: 1MB ÷ 125 bytes = 8,388 records
 * - **Limiting factor**: Payload size
 * - **MAX_RECORDS_PER_BATCH_PT30M = 8,388**
 *
 * ### P1D:
 * - Query time per record: 10ms (aggregates ~48 intervals)
 * - Max records from time: 20,000ms ÷ 10ms = 2,000 records
 * - Max records from payload: 1MB ÷ 125 bytes = 8,388 records
 * - **Limiting factor**: Query time (backend aggregation is expensive)
 * - **MAX_RECORDS_PER_BATCH_P1D = 2,000**
 *
 * **Key Insight**: P1D queries require substantially more backend work because each P1D
 * record aggregates ~48 PT30M intervals. This makes P1D queries ~20× slower per record
 * despite returning the same payload size. Therefore:
 * - **PT30M** is payload-limited (can handle 8,388 records before hitting 1MB limit)
 * - **P1D** is time-limited (can only handle 2,000 records before hitting 20s timeout)
 *
 * ## Algorithm:
 *
 * Given:
 * - `R` = rule count
 * - `D` = duration in hours
 * - `I` = interval length in hours (0.5 for PT30M, 24 for P1D)
 * - `MAX_RECORDS` = aggregation-specific (8,388 for PT30M, 2,000 for P1D)
 *
 * Calculate:
 * 1. Total intervals in duration: `totalIntervals = D / I`
 * 2. Max intervals per batch: `maxIntervals = MAX_RECORDS / R`
 * 3. Batch size in intervals: `batchIntervals = min(maxIntervals, totalIntervals)`
 * 4. Batch size in hours: `batchHours = batchIntervals × I`
 *
 * This ensures: `R × batchIntervals ≤ MAX_RECORDS`
 *
 * ## Edge Cases:
 *
 * 1. **Zero rules**: Return full duration (no data to fetch)
 * 2. **Very short duration**: Return full duration (no splitting needed)
 * 3. **Very high rule count**: Return minimum batch size (1 interval)
 * 4. **Invalid inputs**: Return safe defaults with error logging
 */

// Measured/derived constants
const QUERY_TIME_PER_RECORD_MS_PT30M = 0.5; // Empirically observed (no aggregation)
const QUERY_TIME_PER_RECORD_MS_P1D = 10; // Empirically observed (aggregates ~48 intervals)
const BYTES_PER_RECORD = 125; // Average size of TradeSetSummary record
const TARGET_QUERY_TIME_MS = 20000; // 20 seconds (safety margin below 30s backend timeout)
const MAX_PAYLOAD_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB

// Derived maximum records per batch for each aggregation type
const MAX_RECORDS_FROM_TIME_PT30M = Math.floor(
  TARGET_QUERY_TIME_MS / QUERY_TIME_PER_RECORD_MS_PT30M,
); // 40,000
const MAX_RECORDS_FROM_TIME_P1D = Math.floor(
  TARGET_QUERY_TIME_MS / QUERY_TIME_PER_RECORD_MS_P1D,
); // 2,000
const MAX_RECORDS_FROM_PAYLOAD = Math.floor(
  MAX_PAYLOAD_SIZE_BYTES / BYTES_PER_RECORD,
); // 8,388

// Use the more conservative limit for each aggregation
const MAX_RECORDS_PER_BATCH_PT30M = Math.min(
  MAX_RECORDS_FROM_TIME_PT30M,
  MAX_RECORDS_FROM_PAYLOAD,
); // 8,388 (payload limited)
const MAX_RECORDS_PER_BATCH_P1D = Math.min(
  MAX_RECORDS_FROM_TIME_P1D,
  MAX_RECORDS_FROM_PAYLOAD,
); // 2,000 (time limited due to aggregation cost)

// Interval lengths in hours
const PT30M_HOURS = 0.5;
const P1D_HOURS = 24;

logger.log(JSON.stringify({
  MAX_RECORDS_FROM_TIME_PT30M,
  MAX_RECORDS_FROM_TIME_P1D,
  MAX_RECORDS_FROM_PAYLOAD,
  MAX_RECORDS_PER_BATCH_PT30M,
  MAX_RECORDS_PER_BATCH_P1D,
}));

/**
 * Get interval length in hours for a given aggregation.
 * @param {string} aggregation - Aggregation type ('PT30M' or 'P1D')
 * @returns {number} Interval length in hours
 */
const getIntervalHours = (aggregation) => {
  if (aggregation === PT30M) return PT30M_HOURS;
  if (aggregation === P1D) return P1D_HOURS;
  logger.error(`Unknown aggregation: ${aggregation}, defaulting to P1D`);
  return P1D_HOURS;
};

/**
 * Calculate the number of intervals that fit within a duration.
 * @param {number} durationHours - Duration in hours
 * @param {number} intervalHours - Interval length in hours
 * @returns {number} Number of complete intervals
 */
const calculateIntervalCount = (durationHours, intervalHours) => (
  Math.ceil(durationHours / intervalHours)
);

/**
 * Get the maximum records per batch for a given aggregation.
 * @param {string} aggregation - Aggregation type ('PT30M' or 'P1D')
 * @returns {number} Maximum records per batch
 */
const getMaxRecordsPerBatch = (aggregation) => {
  if (aggregation === PT30M) return MAX_RECORDS_PER_BATCH_PT30M;
  if (aggregation === P1D) return MAX_RECORDS_PER_BATCH_P1D;
  logger.error(`Unknown aggregation: ${aggregation}`);
  return MAX_RECORDS_PER_BATCH_P1D; // Default to more conservative limit
};

/**
 * Get the query time per record for a given aggregation.
 * @param {string} aggregation - Aggregation type ('PT30M' or 'P1D')
 * @returns {number} Query time per record in milliseconds
 */
const getQueryTimePerRecord = (aggregation) => {
  if (aggregation === PT30M) return QUERY_TIME_PER_RECORD_MS_PT30M;
  if (aggregation === P1D) return QUERY_TIME_PER_RECORD_MS_P1D;
  logger.error(`Unknown aggregation: ${aggregation}`);
  return QUERY_TIME_PER_RECORD_MS_P1D; // Default to more conservative (slower) estimate
};

/**
 * Calculate optimal batch size for trade set summary queries.
 * This function uses a principled approach based on measured system constraints:
 * - Backend query timeout (30s, targeting 20s for safety)
 * - Response payload size limits (~5 MB)
 * - Empirically measured query performance (0.5ms per record)
 *
 * The algorithm ensures that each batch stays under the constraint:
 *   ruleCount × intervalsPerBatch ≤ MAX_RECORDS_PER_BATCH (20,000)
 * @typedef {object} TradeSetSummaryBatchSize
 * @property {number} batchSizeHours - batch size in hours.
 * @property {number} batchCount - estimated number of batches.
 * @property {number} intervalsPerBatch - intervals per batch.
 * @property {number} recordsPerBatch - estimated records per batch.
 * @property {number} estimatedQueryTimeMs - estimated query time in milliseconds.
 * @property {number} estimatedPayloadSizeBytes - estimated payload size in bytes.
 * @param {object} params - Calculation parameters.
 * @param {DateTime} params.start - Start timestamp (Luxon DateTime).
 * @param {DateTime} params.finish - Finish timestamp (Luxon DateTime).
 * @param {string} params.aggregation - Aggregation interval ('PT30M' or 'P1D').
 * @param {number} params.ruleCount - Number of relevant trade rules.
 * @returns {TradeSetSummaryBatchSize} Batch calculation result
 */
export const calculateTradeSetSummaryBatchSize = ({
  start,
  finish,
  aggregation,
  ruleCount,
}) => {
  // Validate DateTime inputs
  if (!start || !finish || !start.isValid || !finish.isValid) {
    logger.error('Invalid start or finish DateTime provided');
    const fallbackIntervalHours = aggregation === PT30M ? PT30M_HOURS : P1D_HOURS;
    const fallbackQueryTime = aggregation === PT30M
      ? QUERY_TIME_PER_RECORD_MS_PT30M
      : QUERY_TIME_PER_RECORD_MS_P1D;
    return {
      batchSizeHours: fallbackIntervalHours,
      batchCount: 1,
      intervalsPerBatch: 1,
      recordsPerBatch: ruleCount || 0,
      estimatedQueryTimeMs: (ruleCount || 0) * fallbackQueryTime,
      estimatedPayloadSizeBytes: (ruleCount || 0) * BYTES_PER_RECORD,
    };
  }

  // Validate aggregation
  if (!aggregation || (aggregation !== PT30M && aggregation !== P1D)) {
    logger.error(`Invalid aggregation: ${aggregation}. Must be PT30M or P1D`);
    return {
      batchSizeHours: P1D_HOURS,
      batchCount: 1,
      intervalsPerBatch: 1,
      recordsPerBatch: ruleCount || 0,
      estimatedQueryTimeMs: (ruleCount || 0) * QUERY_TIME_PER_RECORD_MS_P1D,
      estimatedPayloadSizeBytes: (ruleCount || 0) * BYTES_PER_RECORD,
    };
  }

  // Validate and normalize rule count
  const validatedRuleCount = typeof ruleCount === 'number' && ruleCount >= 0 ? ruleCount : 0;

  // Calculate total duration
  const durationHours = finish.diff(start, 'hours').hours;

  if (durationHours <= 0) {
    logger.warn('Duration is zero or negative, returning minimum batch size');
    const intervalHours = getIntervalHours(aggregation);
    const queryTimePerRecord = getQueryTimePerRecord(aggregation);
    return {
      batchSizeHours: intervalHours,
      batchCount: 1,
      intervalsPerBatch: 1,
      recordsPerBatch: validatedRuleCount,
      estimatedQueryTimeMs: validatedRuleCount * queryTimePerRecord,
      estimatedPayloadSizeBytes: validatedRuleCount * BYTES_PER_RECORD,
    };
  }

  // Get aggregation-specific constants
  const intervalHours = getIntervalHours(aggregation);
  const maxRecordsPerBatch = getMaxRecordsPerBatch(aggregation);
  const queryTimePerRecord = getQueryTimePerRecord(aggregation);

  // Calculate total intervals in the duration
  const totalIntervals = calculateIntervalCount(durationHours, intervalHours);

  // Edge case: Zero rules means no data to fetch
  if (validatedRuleCount === 0) {
    logger.log('Rule count is 0, returning full duration (no data to fetch)');
    return {
      batchSizeHours: durationHours,
      batchCount: 1,
      intervalsPerBatch: totalIntervals,
      recordsPerBatch: 0,
      estimatedQueryTimeMs: 0,
      estimatedPayloadSizeBytes: 0,
    };
  }

  // Calculate maximum intervals per batch to stay under maxRecordsPerBatch
  // Formula: maxIntervals = MAX_RECORDS / ruleCount
  const maxIntervalsPerBatch = Math.floor(maxRecordsPerBatch / validatedRuleCount);

  // Ensure at least 1 interval per batch (even if it exceeds the limit)
  const safeMaxIntervalsPerBatch = Math.max(1, maxIntervalsPerBatch);

  // Determine actual intervals per batch (cannot exceed total intervals)
  const intervalsPerBatch = Math.min(safeMaxIntervalsPerBatch, totalIntervals);

  // Calculate batch size in hours
  const batchSizeHours = intervalsPerBatch * intervalHours;

  // Calculate number of batches needed
  const batchCount = Math.ceil(totalIntervals / intervalsPerBatch);

  // Calculate derived metrics
  const recordsPerBatch = validatedRuleCount * intervalsPerBatch;
  const estimatedQueryTimeMs = recordsPerBatch * queryTimePerRecord;
  const estimatedPayloadSizeBytes = recordsPerBatch * BYTES_PER_RECORD;

  // Log warning if we exceed constraints (can happen with very high rule counts)
  if (recordsPerBatch > maxRecordsPerBatch) {
    logger.warn(
      `Batch size (${recordsPerBatch} records) exceeds recommended maximum (${maxRecordsPerBatch}). `
      + `Rule count (${validatedRuleCount}) is very high. Query may timeout.`,
    );
  }

  logger.log(
    `Batch calculation: ${validatedRuleCount} rules, ${aggregation} aggregation, `
    + `${durationHours.toFixed(1)}h duration → ${batchSizeHours.toFixed(1)}h batches `
    + `(${batchCount} batches, ${intervalsPerBatch} intervals/batch, `
    + `~${recordsPerBatch} records/batch, ~${estimatedQueryTimeMs.toFixed(0)}ms query time, `
    + `~${(estimatedPayloadSizeBytes / 1024).toFixed(0)}KB payload)`,
  );

  return {
    batchSizeHours,
    batchCount,
    intervalsPerBatch,
    recordsPerBatch,
    estimatedQueryTimeMs,
    estimatedPayloadSizeBytes,
  };
};

/**
 * Generate an array of time range batches for iterative querying.
 * This helper function splits a time range into multiple batches based on
 * the calculated batch size, useful for sequential or parallel batch processing.
 * @param {object} params - Batch generation parameters
 * @param {DateTime} params.start - Start timestamp (Luxon DateTime)
 * @param {DateTime} params.finish - Finish timestamp (Luxon DateTime)
 * @param {number} params.batchSizeHours - Batch size in hours
 *                                        (from calculateTradeSetSummaryBatchSize)
 * @returns {Array<{start: DateTime, finish: DateTime, batchIndex: number}>}
 *  Array of batch time ranges
 */
export const generateBatchTimeRanges = ({ start, finish, batchSizeHours }) => {
  if (!start || !finish || !start.isValid || !finish.isValid) {
    logger.error('Invalid start or finish DateTime provided to generateBatchTimeRanges');
    return [];
  }

  if (typeof batchSizeHours !== 'number' || batchSizeHours <= 0) {
    logger.error(`Invalid batchSizeHours: ${batchSizeHours}`);
    return [];
  }

  const batches = [];
  let currentStart = start;
  let batchIndex = 0;

  // Use DST-aware day arithmetic when batch size is a multiple of 24 hours
  // This prevents drift across DST transitions for day-sized batches
  const batchSizeDays = batchSizeHours % 24 === 0 ? batchSizeHours / 24 : 0;

  while (currentStart < finish) {
    // For day-sized batches, use days to maintain alignment with timezone-based aggregation
    // For sub-day batches, use hours (DST transitions don't affect sub-day intervals)
    const currentFinish = batchSizeDays > 0
      ? currentStart.plus({ days: batchSizeDays })
      : currentStart.plus({ hours: batchSizeHours });

    // Ensure the last batch doesn't exceed the overall finish time
    const adjustedFinish = currentFinish > finish ? finish : currentFinish;

    batches.push({
      start: currentStart,
      finish: adjustedFinish,
      batchIndex,
    });

    currentStart = adjustedFinish;
    batchIndex += 1;
  }

  logger.log(`Generated ${batches.length} batch time ranges: ${JSON.stringify(batches)}`);
  return batches;
};

/**
 * Estimate the total number of records for a trade set summary query.
 * This provides a rough estimate of the total data volume before executing queries,
 * useful for progress tracking and validation.
 * @param {object} params - Estimation parameters
 * @param {DateTime} params.start - Start timestamp (Luxon DateTime)
 * @param {DateTime} params.finish - Finish timestamp (Luxon DateTime)
 * @param {string} params.aggregation - Aggregation interval ('PT30M' or 'P1D')
 * @param {number} params.ruleCount - Number of active trade rules
 * @returns {number} Estimated total number of records
 */
export const estimateTotalRecords = ({
  start,
  finish,
  aggregation,
  ruleCount,
}) => {
  if (!start || !finish || !start.isValid || !finish.isValid) {
    return 0;
  }

  const validatedRuleCount = typeof ruleCount === 'number' && ruleCount >= 0 ? ruleCount : 0;
  const durationHours = finish.diff(start, 'hours').hours;

  if (durationHours <= 0) {
    return 0;
  }

  const intervalHours = getIntervalHours(aggregation);
  const totalIntervals = calculateIntervalCount(durationHours, intervalHours);

  return validatedRuleCount * totalIntervals;
};
